import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useSeo, articleJsonLd } from "@/lib/seo";
import { PageHeader, Loader, Chip } from "@/components/shared";
import { formatDate } from "@/lib/format";
import { Search, Clock, ArrowLeft } from "lucide-react";

export function Resources() {
  useSeo({
    title: "The Journal — Family Constellation Articles & Resources",
    description: "Writing on patterns, belonging, inherited stories, and the quiet work of resolution — from Arjunna Soni's Family Constellation practice in Ludhiana.",
    path: "/resources",
  });
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const category = params.get("category") || "";

  const load = () => {
    const q = {};
    if (category) q.category = category;
    if (params.get("search")) q.search = params.get("search");
    api.get("/posts", { params: q }).then((r) => setData(r.data)).catch(() => setData({ posts: [] }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, params]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const doSearch = (e) => {
    e.preventDefault();
    const p = {};
    if (search) p.search = search;
    if (category) p.category = category;
    setParams(p);
  };

  return (
    <div>
      <PageHeader crumb="Resources" title="The journal" intro="Writing on patterns, belonging, inherited stories, and the quiet work of resolution." />
      <section className="container-page pb-16 md:pb-24">
        <h2 className="text-2xl md:text-3xl text-earth mb-8" style={{ fontWeight: 600 }}>Articles on Family Constellation work</h2>
        <div className="flex flex-col md:flex-row gap-4 justify-between mb-10">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setParams(search ? { search } : {})} className={`px-4 py-2 rounded-full text-sm font-semibold ${!category ? "bg-gold text-earth" : "bg-surface-card border hairline text-earth/70"}`} data-testid="cat-all">All</button>
            {cats.map((c) => (
              <button key={c.name} onClick={() => setParams({ category: c.name })} className={`px-4 py-2 rounded-full text-sm font-semibold ${category === c.name ? "bg-gold text-earth" : "bg-surface-card border hairline text-earth/70"}`} data-testid={`cat-${c.slug}`}>{c.name}</button>
            ))}
          </div>
          <form onSubmit={doSearch} className="relative" data-testid="resources-search">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-earth/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles…" className="pl-9 pr-4 py-2.5 rounded-md bg-surface-card border hairline outline-none focus:border-gold w-full md:w-64" />
          </form>
        </div>

        {!data ? <Loader /> : data.posts.length === 0 ? (
          <p className="text-earth/60 py-16 text-center">No articles found.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {data.posts.map((p) => (
              <Link key={p.post_id} to={`/resources/${p.slug}`} className="group" data-testid={`post-card-${p.slug}`}>
                <div className="flex items-center gap-2 mb-3">{p.categories?.[0] && <Chip>{p.categories[0]}</Chip>}<span className="label-caps text-earth/40 flex items-center gap-1"><Clock size={12} /> {p.readingTime} min</span></div>
                <h3 className="text-2xl text-earth group-hover:text-gold-dark transition-colors leading-snug">{p.title}</h3>
                <p className="mt-2 text-earth/70">{p.excerpt}</p>
                <p className="mt-3 label-caps text-earth/40">{formatDate(p.publishedAt)}</p>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function ResourceDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [nf, setNf] = useState(false);
  useSeo({
    title: p ? p.title : "Article",
    description: p ? p.excerpt : undefined,
    path: `/resources/${slug}`,
    type: "article",
    suffix: "Arjunna Soni",
    jsonLd: p ? articleJsonLd(p) : null,
  });
  useEffect(() => { api.get(`/posts/${slug}`).then((r) => setP(r.data)).catch(() => setNf(true)); }, [slug]);

  if (nf) return <div className="container-page py-24 text-center text-earth/70">Article not found. <Link to="/resources" className="text-gold-dark underline">All articles</Link></div>;
  if (!p) return <Loader />;

  return (
    <div>
      <article className="container-page pt-14 pb-16 md:pt-20 md:pb-24 max-w-3xl">
        <Link to="/resources" className="inline-flex items-center gap-2 label-caps text-earth/50 hover:text-earth mb-6"><ArrowLeft size={14} /> All articles</Link>
        <div className="flex items-center gap-3 mb-4">{p.categories?.map((c) => <Chip key={c}>{c}</Chip>)}<span className="label-caps text-earth/40 flex items-center gap-1"><Clock size={12} /> {p.readingTime} min read</span></div>
        <h1 className="text-4xl md:text-5xl text-earth leading-tight mb-4" style={{ fontWeight: 600, letterSpacing: "-0.02em" }}>{p.title}</h1>
        <p className="label-caps text-earth/50 mb-10">{p.author} · {formatDate(p.publishedAt)}</p>
        <div className="prose-editorial text-earth/85" dangerouslySetInnerHTML={{ __html: p.body }} />

        <div className="mt-12 bg-surface-card border hairline rounded-lg p-8 text-center">
          <h3 className="text-2xl text-earth mb-2">Ready to explore your own system?</h3>
          <p className="text-earth/70 mb-5">A first session is a gentle, grounded place to begin.</p>
          <Link to="/book" className="btn-gold">Book a Session</Link>
        </div>
      </article>

      {p.related?.length > 0 && (
        <section className="bg-surface-card border-t hairline py-16">
          <div className="container-page max-w-3xl">
            <h2 className="text-2xl text-earth mb-6" style={{ fontWeight: 600 }}>Related reading</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {p.related.map((r) => (
                <Link key={r.post_id} to={`/resources/${r.slug}`} className="group">
                  <h3 className="text-lg text-earth group-hover:text-gold-dark transition-colors leading-snug">{r.title}</h3>
                  <p className="text-sm text-earth/60 mt-1 line-clamp-2">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
